import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "@/store";
import React, { Suspense, useEffect } from "react";
import ProtectedRoute from "./protected-route";
import routes from "./routes.json";

import { Navigate, Outlet,useNavigate } from 'react-router-dom';
import { getCategories } from "@/store/thunkMidddleware/venueMiddleware"
import { getWebsiteBySubdomain } from "@/store/thunkMidddleware/authMiddleware"

import { useAppDispatch, useAppSelector } from "@/hooks/useReduxHooks";
import { useIsVendorWebsite } from "@/hooks/useVendorWebsite";
import { useParams } from 'react-router-dom';

import { getSubDomain } from "@/lib/location";
import RouteFallback from "@/components/RouteFallback";
// import HeroSectionSkeleton from "@/components/skeletons/HeroSkeleton";
import { Loader2 } from "lucide-react";

/* Every page is lazy. A visitor downloads the app shell plus the one page
   they opened, not the whole site: that matters most for /m/:publicId, which
   people open from WhatsApp on a weak connection, but it speeds up every
   landing page. Routes without their own Suspense fall back to the one
   around <Routes>. */
const Shop = React.lazy(() => import("../pages/Venues"));
const VenueDetail = React.lazy(() => import("../pages/NewVenueDetails"));
const NotFound = React.lazy(() => import("../pages/NotFound"));
const Sports = React.lazy(() => import("../pages/services/Sports"));
const Fashion = React.lazy(() => import("../pages/services/Fashion"));
const Accessories = React.lazy(() => import("../pages/services/Accessories"));
const Login = React.lazy(() => import("../pages/Login"));
const Account = React.lazy(() => import("../pages/Account"));
const Checkout = React.lazy(() => import("../pages/payment/checkout"));
const NewSlotSelector = React.lazy(() => import("../components/venues/NewSlotSelector"));
const AccountDeletion = React.lazy(() => import("@/pages/AccountDeletion"));
const NoVenuesFound = React.lazy(() => import("@/components/NoVenues"));
// Renders the full site Layout, so it is lazy too; never use it as a
// Suspense fallback (that would put the whole header in the initial bundle).
const PageLoading = React.lazy(() => import("@/pages/PageLoading"));


const Dashboard = React.lazy(() => import("@/pages/Dashboard"));
const PrivacyPolicy = React.lazy(() => import("@/pages/PrivacyPolicy"));
const MyBookings = React.lazy(() => import("@/pages/MyBookings"));
const BookingDetailsScreen = React.lazy(() => import("@/pages/Bookings/BookingDetailsScreen"));
const SeeAllReviews = React.lazy(() => import("@/pages/SeeAllReviews"));

const WishList = React.lazy(() => import("@/pages/WishList"));

const DirectBookingVenueDetailsAndSlots = React.lazy(() => import("@/pages/DirectBookingLink/DirectBookingVenueDetailsAndSlots"));
const BookingReschedule = React.lazy(() => import("@/pages/Bookings/BookingReschedule"));

// Public match page (/m/:publicId), opened from WhatsApp links: live score,
// team fill-up and one-tap join. No login needed to view.
const PublicMatchPage = React.lazy(() => import("@/match/JoinPage"));
const PublicMatchRoute = () => {
  const { publicId = "" } = useParams();
  return <PublicMatchPage publicId={publicId} />;
};



// const AuthRoute = ({ authState }: { authState: { isAuthenticated: boolean; token: string | null } }) => {
//   return authState.isAuthenticated || authState.token ? (
//     <Navigate to="/" replace />  // Redirect if authenticated
//   ) : (
//     <Outlet />  // Allow access if not authenticated
//   );
// };



const Router = () => {
  const dispatch = useAppDispatch()
  const isVendorWebsite = useIsVendorWebsite()
  const venuesState = useAppSelector(state => state.venues);
  const globalState = useAppSelector(state => state.global);

  const authState = useAppSelector(state => state.auth);
const subdomain= getSubDomain()

  const fetchCategories=()=>{
      dispatch(getCategories({
      callback: (categoriesRes) => {
        console.log("categoriesRes:", categoriesRes)
      }
    }))
  }

  const fetchSubdomainDetails=()=>{
    if(subdomain && !authState.getWebsiteBySubdomainResponse?.payload){
      dispatch(getWebsiteBySubdomain({pathparams:subdomain.toLowerCase(),callback:(res)=>{
          console.log(res)
            if (!venuesState.Categories.payload) {
              fetchCategories()
            }
         
       }}))
  }else{
    if (!venuesState.Categories.payload) {
        fetchCategories()
      }
  }
  }

  useEffect(() => {
    fetchSubdomainDetails()
  }, [])


  // A match link must open the match even if the viewer's city has no venues.
  const isPublicMatchLink = window.location.pathname.startsWith("/m/");

  if(venuesState.showNoVenueScreen && !isPublicMatchLink){
    return<BrowserRouter> 
     <Suspense fallback={<RouteFallback />}>
     <NoVenuesFound
    currentLocation = {globalState?.currentSelectedCity || globalState?.locationDetails?.location?.city }
    onCitySelect={()=>{}}
    onSearch={()=>{}}
    
    />
     </Suspense>
    </BrowserRouter>
  }
  if(!isVendorWebsite && subdomain){
    return( <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
      <Routes> 
        {!authState.getWebsiteBySubdomainResponse?.payload ?
         <Route path="/" element={<PageLoading />} />

        : <Route path="/" element={<NotFound />} />
      }
      {/* <Route path="/" element={<NotFound />} /> */}
      <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </BrowserRouter>)
  }else{
  return (<BrowserRouter>
    <Suspense fallback={<RouteFallback />}>
    <Routes>

      {/* --------------Public routes ------------------------------------ */}
      <Route path={routes.login}
        element={
          authState.isAuthenticated || authState.token ? (
            <Navigate to={window.location.pathname ===routes.login?"/" :window.location.pathname} replace />  // Redirect to home/dashboard if authenticated
          ) : (
            <Login />  // Show login page only if not authenticated
          )
        }
      />
      <Route
        path={routes.dashboard}
        element={<Suspense fallback={<Loader2 className="text-white animate-spin absolute z-10 top-1/2 right-1/2 w-12 h-12 text-black/80" />      }>  <Dashboard />  </Suspense>}
      />

  <Route
        path={routes.deleteAccount}
        element={<Suspense fallback={<Loader2 className="text-white animate-spin absolute z-10 top-1/2 right-1/2 w-12 h-12 text-black/80" />      }>  <AccountDeletion />  </Suspense>}
      />
      
      {/* Public and unauthenticated by design: Google Play requires the policy
          to sit on an active, publicly accessible, non-geofenced URL. */}
      <Route
        path={routes.privacyPolicy}
        element={<Suspense fallback={<RouteFallback />}><PrivacyPolicy /></Suspense>}
      />

      <Route path={routes.publicMatch} element={<Suspense fallback={<RouteFallback />}><PublicMatchRoute /></Suspense>} />
      <Route path={routes.venues} element={<Shop />} />
      <Route path={routes.venuesDetailsByid} element={<VenueDetail />} />

      <Route
        path={routes.wishlist}
        element={<Suspense fallback={<Loader2 className="animate-spin text-white absolute z-10 top-1/2 right-1/2 w-12 h-12 text-black/80" />}>  <WishList />  </Suspense>}
      />
      <Route path={routes.directBookingLink} element={<DirectBookingVenueDetailsAndSlots />} />
      <Route path={routes.allReviewsById} 
        element={<Suspense 
        fallback={<Loader2 className="animate-spin absolute z-10 top-[50%] right-1/2 w-12 h-12 text-black/80" />  }>  
           <SeeAllReviews /> 
         </Suspense>} 
        />

      {/*-------------- Protected routes --------------*/}
      <Route element={<ProtectedRoute />}>
        <Route path={routes.checkSlots} element={<NewSlotSelector />} />
        <Route path={routes.account} element={<Account />} />
        <Route path={routes.checkout} element={<Checkout />} />
        <Route path={routes.mybookings} 
        element={<Suspense 
        fallback={<Loader2 className="animate-spin absolute z-10 top-[50%] right-1/2 w-12 h-12 text-black/80" />  }>    
        <MyBookings />
          </Suspense>} 
        />


        <Route path={routes.bookingDetailsById}
        element={<Suspense
        fallback={<Loader2 className="animate-spin absolute z-10 top-[50%] right-1/2 w-12 h-12 text-black/80" />  }>
        <BookingDetailsScreen />
          </Suspense>}
        />

           <Route path={routes.bookingRescheduleByid} 
        element={<Suspense 
        fallback={<Loader2 className="animate-spin absolute z-10 top-[50%] right-1/2 w-12 h-12 text-black/80" />  }>    
        <BookingReschedule />
          </Suspense>} 
        />

        

      </Route>









      {/* <Route path="/rent" element={<Rent />} /> */}
      {/* <Route path="/product/:id" element={<ProductDetail />} /> */}

      {/* <Route path="/cart" element={<Cart />} /> */}

      {/* Service Routes */}
      <Route path="/services/sports" element={<Sports />} />
      <Route path="/services/fashion" element={<Fashion />} />
      <Route path="/services/accessories" element={<Accessories />} />
      {/* <Route element={<ProtectedRoute />}> */}
      {/* <Route
                    path={routes.dashboard}
                    element={
                        <Suspense fallback={<DashboardSkeleton />}>
                            <Dashboard />
                        </Suspense>
                    }
                /> */}

      {/* <Route
                  path={routes.users}
                  element={
                    <Suspense fallback={<UsersSkeleton />}>
                      <Users />
                    </Suspense>
                  }
                /> */}

      {/* <Route path={routes.userProfile} 
                element={
                  <Suspense fallback={<>Loading...</>}>
                    <UserProfile />
                  </Suspense>
                }
                /> */}




      {/* </Route> */}

      {/* Not Found route */}
      <Route path="*" element={<NotFound />} />

      <Route path="/NotFound" element={<NotFound />} />

    </Routes>
    </Suspense>
  </BrowserRouter>
  );
}
}
export default Router;
